/**
 * WALL FRAMING round (docs/MODELER.md) — the E2 roof-framing pattern applied to walls:
 * derived wood-stud framing, representation-grade (drawings + the open-frame look),
 * explicitly NOT a structural tool — no blocking, bracing, nailing, fire-stops, or
 * engineering, ever (the standing guard).
 *
 * NOTHING here is an element. Members derive at generation time from the wall solid +
 * its openings — reshape the wall (or the roof it attaches to) and it re-frames:
 *  - single BOTTOM PLATE following the wall's derived BASE (stepped stem/curb tops,
 *    base-attached slopes), cut at door openings;
 *  - DOUBLE TOP PLATE following the PLATE RULE (PLATE-HEIGHT round, 2026-08-13 —
 *    doctrine: BEARING INTENT IS DECLARED BY MODELED OVERLAP, no toggles): on a
 *    BEARING-type stretch (the attached roof plane is LEVEL along the run and the
 *    wall hosts the bearing line) the plate tops out LEVEL at the BEARING HEIGHT
 *    exactly — the plate line the birdsmouth seats on; on a RAKE-type stretch (the
 *    plane slopes along the run) the plate RAKES with the roof, its top at the
 *    underside of the roof's MAIN BODY framing (the face plane + the roof's
 *    below-core rise — never the E1-cropped eave underside);
 *  - STUDS at the wall's own studSpacingIn (an instance param) from a consistent layout
 *    end (the wall's start),
 *    CUT-TO-FIT under the plate line (a raked plate rakes the stud's top cut);
 *  - KING + TRIMMER at every opening jamb, HEADER sized from the curated firm-standard
 *    span→member rule table (kernel wallFraming.ts), flat SILL at windows, CRIPPLES
 *    above headers and below sills. Openings too close to fit the jamb pack between
 *    them (< 4 stud widths clear, with vertical overlap) MULL: one framed unit,
 *    kings/trimmers at the outer ends only, one continuous header over the full run.
 *
 * STUD SIZE DERIVES from the assembly — the structural core thickness IS the stud
 * depth, and members occupy exactly the core band across the wall thickness. Non-wood
 * cores (isWoodCoreWall) have no framing derivation at all — this module simply never
 * runs for them.
 *
 * DEFAULT-ON, NO INSTANCE TOGGLE (2026-08-12, superseding the original per-wall
 * `showFraming` study toggle): every wood-core wall frames automatically — there is
 * nothing to switch on per element. Visibility AND generation are controlled by the
 * live 3D viewport's SYSTEMS list (FRAMING SESSION Phase 4 — the Wall Framing check
 * gates generation lazily via registry.GeneratedSystems and display via sceneStore's
 * chunk-class filter), never a per-element flag. Members are honest prisms merged
 * into ONE meshOnly solid per wall (groupKey `…|framing` — the chunk class the
 * systems filter keys on), and gestures defer for free (every drag previews as a
 * ghost and commits ONE param write at release, so nothing regenerates mid-gesture).
 *
 * Geometry model: members lay along the wall's STRAIGHT runs (the stored path's
 * bulge-free edges — curved spans have no framing, the openings-on-curves precedent).
 * Vertical profiles evaluate the SAME surfaces the body derivation clips against
 * (targetSurfaces/targetTopSurfaces results + the foundation layout's stepped runs,
 * passed in by wallSolid), pointwise with exact crossing stations — never a sampled
 * approximation of the plate line.
 */
import type { Path2D, Pt2, ResolvedElement } from '@aj/bim-kernel'
import {
  PLATE_THICKNESS_IN, STUD_WIDTH_IN, flattenPathPts, headerRuleFor, isWoodCoreWall,
} from '@aj/bim-kernel'
import { signedArea } from '../../clipper'
import { newMeshAcc, meshAccUvs } from '../roof/meshAcc'
import { assemblyZonesFt, framingMaterialId, prism } from '../roof/framing'
import type { RoofPlateFace } from '../roof/underside'
import type { FloorPlatformBand } from '../floor/framing'
import type { OpeningPlacement } from '../../openings'
import type { ElementSolid } from './generate3d'

const EPS = 1e-6
const MIN_MEMBER_FT = 1 / 48          // members shorter/thinner than 1/4″ are noise
const DOOR_SILL_TOL_FT = 2 / 12      // an opening within 2″ of the base is a door

/** a clip surface: plan rings + a plane-z evaluator (targetSurfaces' shape) */
export interface FramingSurf { rings: Pt2[][]; zAt: (p: Pt2) => number }

/**
 * PLATE-HEIGHT round: what the top plate derives from, per attach target —
 * roof faces carry their MAIN BODY plane + bearing metadata (roofPlateFaces);
 * a slab target is a level plate at `z` (its mass underside, plus the slab's
 * own below-core rise when it's a framed floor — plates land under joists).
 */
export type PlateSurf =
  | ({ kind: 'roof' } & RoofPlateFace)
  | { kind: 'flat'; rings: Pt2[][]; z: number }

export interface WallFramingCtx {
  el: ResolvedElement
  /** loop-normalized stored path — straight runs derive from its bulge-free edges
   *  (node stations ARE frame stations: the frame flattens this same path) */
  workPath: Path2D
  /** centerline offset from the reference line (2.6a location lines) */
  delta: number
  /** assembly thickness */
  t: number
  zTopNominal: number
  /** the FINAL prism top (after the attach derivation — the solid's zTop) */
  prismTop: number
  zBaseNominal: number
  /** the FINAL prism base (after stepped-base/base-attach — the solid's zBase) */
  prismBase: number
  placements: OpeningPlacement[]
  /** PLATE-HEIGHT round: plate-rule surfaces, in first-target-wins order
   *  (empty = flat plate at the prism top) */
  plateSurfs: PlateSurf[]
  /** base-attach clip surfaces (TOP surfaces of the picked targets), first-hit order */
  baseSurfs: FramingSurf[]
  /** footing-mode stepped bottoms (foundation-layout runs, station space ≈ the wall's) */
  baseRuns: { s0: number; s1: number; z: number }[]
  /** FRAMING SESSION Phase 3 — PLATFORM FRAMING: wood-core framed floors crossing
   *  this wall's MID-SPAN (pre-filtered by the caller). Where a band's rings cover
   *  the run, the derived framing SEGMENTS at the floor: the lower segment caps
   *  with DOUBLE plates directly under the floor framing, the upper segment starts
   *  with a SINGLE sole plate on the floor's SHEATHING, and vertical members never
   *  pass through the band. The wall stays ONE element — derivation only. */
  floorBands: FloorPlatformBand[]
  /**
   * FLOOR-TO-FLOOR TOP PLATE round (2026-08-17, Matt: "the wall has to pass the
   * level above for this to happen, but sometimes we model walls from floor to
   * floor"): floors that sit AT this wall's own top (not mid-span crossings —
   * those are `floorBands` above) with no explicit top-attach covering them.
   * Pre-filtered by the caller to exactly the no-`plateSurfs` case this matters
   * for. Where a band's ring covers the run, the double top plate caps at the
   * floor's JOIST BOTTOM instead of the raw prism top — no sole plate, no
   * resumed studs above (unlike `floorBands`, the wall genuinely ends here). */
  topFloorBands: FloorPlatformBand[]
  /**
   * SPAN BEAMS (2026-10-14, Matt: "whether it's exterior or interior, it should still show a
   * beam at the underside of it if it's not supported otherwise"): station ranges of the
   * wall's BASE with nothing bearing under it (decided across elements by endWrap.ts's
   * bearing test, never from the base offset alone). Each gets a beam at the base, sized by
   * the same span table as opening headers, bearing a stud width past each end; the bottom
   * plate drops out over it and studs stand on it.
   */
  spanBeams?: { s0: number; s1: number }[]
}

/** each built framing solid's mesh → the context it was built from, so the cross-element
 *  pass can re-frame a wall once it knows where the wall spans (SPAN BEAMS) */
export const framingCtxByMesh = new WeakMap<object, WallFramingCtx>()

function pointInRing(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

export interface Run { s0: number; s1: number; aPt: Pt2; dir: Pt2; n: Pt2 }

/**
 * The wall's STRAIGHT runs in the frame's station space: walk the work path's edges,
 * accumulating each edge's FLATTENED length (arc edges tessellate exactly as
 * flattenPath tessellated them for the frame, so stations line up); bulge-free edges
 * become runs, arc edges advance the station and frame nothing.
 */
export function straightRuns(workPath: Path2D): Run[] {
  const nodes = workPath.nodes
  const edgeCount = workPath.closed ? nodes.length : nodes.length - 1
  const runs: Run[] = []
  let s = 0
  for (let i = 0; i < edgeCount; i++) {
    const a = nodes[i], b = nodes[(i + 1) % nodes.length]
    const bulge = a.bulge ?? 0
    if (Math.abs(bulge) > 1e-12) {
      const pts = flattenPathPts({ closed: false, nodes: [{ ...a }, { pt: b.pt }] } as Path2D)
      let len = 0
      for (let k = 0; k < pts.length - 1; k++) len += Math.hypot(pts[k + 1][0] - pts[k][0], pts[k + 1][1] - pts[k][1])
      s += len
      continue
    }
    const len = Math.hypot(b.pt[0] - a.pt[0], b.pt[1] - a.pt[1])
    if (len < EPS) continue
    const dir: Pt2 = [(b.pt[0] - a.pt[0]) / len, (b.pt[1] - a.pt[1]) / len]
    // the run's start point IS node a's point — the frame is built on the flattened
    // stored path, so accumulated flattened length lands exactly on the node
    runs.push({ s0: s, s1: s + len, aPt: a.pt, dir, n: [-dir[1], dir[0]] })
    s += len
  }
  return runs
}

/** a piecewise-LINEAR vertical profile segment along a run */
interface VSeg { s0: number; s1: number; z0: number; z1: number }

const lerpZ = (seg: VSeg, s: number): number =>
  seg.s1 - seg.s0 > EPS ? seg.z0 + ((s - seg.s0) / (seg.s1 - seg.s0)) * (seg.z1 - seg.z0) : seg.z0

/** split piecewise-linear segments where they cross `bound`, clamping each side —
 *  mode 'max' floors at the bound (derived tops), 'min' caps at it (derived bases) */
function clampSplit(segs: VSeg[], bound: number, mode: 'max' | 'min'): VSeg[] {
  const out: VSeg[] = []
  const cl = (z: number) => (mode === 'max' ? Math.max(z, bound) : Math.min(z, bound))
  for (const seg of segs) {
    const { s0, s1, z0, z1 } = seg
    const c0 = z0 - bound, c1 = z1 - bound
    if ((c0 > EPS && c1 < -EPS) || (c0 < -EPS && c1 > EPS)) {
      const tc = c0 / (c0 - c1)
      const sc = s0 + (s1 - s0) * tc
      out.push({ s0, s1: sc, z0: cl(z0), z1: bound })
      out.push({ s0: sc, s1, z0: bound, z1: cl(z1) })
    } else {
      out.push({ s0, s1, z0: cl(z0), z1: cl(z1) })
    }
  }
  return out
}

function registerCtx<T extends object>(mesh: T, ctx: WallFramingCtx): T {
  framingCtxByMesh.set(mesh, ctx)
  return mesh
}

export function buildWallFraming(ctx: WallFramingCtx): ElementSolid | null {
  const { el, delta, t, placements } = ctx
  // DEFAULT-ON round (Matt, 2026-08-12): no per-wall instance toggle — every
  // wood-core wall frames automatically. Visibility AND lazy generation are
  // controlled by the viewport's SYSTEMS list (Phase 4 — the Wall Framing check);
  // a per-instance toggle would just fight with it for no reason.
  if (!isWoodCoreWall(el)) return null              // non-wood core: no framing derivation
  const zones = assemblyZonesFt(el)
  if (zones.coreFt < MIN_MEMBER_FT || t <= 0) return null

  const spacingFt = (Number(el.params?.studSpacingIn) || 16) / 12
  const plateFt = PLATE_THICKNESS_IN / 12
  const w = STUD_WIDTH_IN / 12

  // core band offsets from the CENTERLINE (generate2d's convention: exterior face at
  // sign·t/2 along the left normal; layers run exterior→interior)
  const sign = el.params.flipped === true ? -1 : 1
  const oExt = sign * (t / 2 - zones.aboveFt)
  const oInt = sign * (t / 2 - zones.aboveFt - zones.coreFt)
  const oMid = (oExt + oInt) / 2

  const runs = straightRuns(ctx.workPath)
  if (!runs.length) return null

  // ---------- PLATE-HEIGHT round: the plate rule ----------
  // Doctrine (docs/MODELER.md, 2026-08-13): BEARING INTENT IS DECLARED BY MODELED
  // OVERLAP — no toggles. Everything below derives from the attach targets' planes:
  //  - a stretch where the governing roof plane is LEVEL along the run AND the wall
  //    stands within its own thickness of the face's bearing line is a BEARING
  //    stretch — the double top plate tops out LEVEL at the BEARING HEIGHT exactly
  //    (the plate line; the birdsmouth seats on it);
  //  - a stretch where the plane SLOPES along the run is a RAKE stretch — the plate
  //    RAKES with the roof, its top at the underside of the roof's MAIN BODY
  //    framing (the face plane + the roof's below-core rise). The plane is the
  //    UNCROPPED body plane, so the E1 eave crop never bends the plate;
  //  - a level stretch under a face but AWAY from its bearing line (an interior
  //    wall parallel to the eave, mid-slope) plates level at the local framing
  //    underside;
  //  - slab targets plate level at their PlateSurf z (mass underside + a framed
  //    floor's below-core rise — plates land under joists, not under the gyp).
  // EAVE-WALL lesson (the prior round's fix, carried forward): a sample can fall
  // just OUTSIDE every face ring even though the wall genuinely hosts the bearing
  // line — the wall's cross-section straddles it by design. The bearing-line
  // proximity fallback in governingPlate covers exactly that case.
  const LEVEL_SLOPE_EPS = 1e-3           // |dz/ds| below this = level along the run
  const bearingSnapTol = Math.max(t, 0.75)
  const governingPlate = (p: Pt2): PlateSurf | null => {
    for (const surf of ctx.plateSurfs) {
      if (surf.kind === 'roof') {
        if (pointInRing(p, surf.ring)) return surf
      } else {
        for (const r of surf.rings) if (pointInRing(p, r)) return surf
      }
    }
    for (const surf of ctx.plateSurfs) {
      if (surf.kind !== 'roof') continue
      const dBear = Math.abs((p[0] - surf.origin[0]) * surf.uphill[0] + (p[1] - surf.origin[1]) * surf.uphill[1])
      if (dBear > bearingSnapTol) continue
      if (surf.eSpan) {
        const ePos = p[0] * surf.eAxis[0] + p[1] * surf.eAxis[1]
        if (ePos < surf.eSpan[0] - bearingSnapTol || ePos > surf.eSpan[1] + bearingSnapTol) continue
      }
      return surf
    }
    return null
  }
  /** the plate-top profile along a run — the ONE source for the double top plate
   *  AND every member's top cut (studs/kings/cripples stop at its underside) */
  function plateProfileAlongRun(run: Run): VSeg[] {
    if (!ctx.plateSurfs.length) {
      // FLOOR-TO-FLOOR TOP PLATE round: no explicit top-attach — the plate is flat
      // at the prism top EXCEPT wherever a topFloorBand's ring covers the run,
      // where it caps at that floor's joist bottom instead (stops the double top
      // plate — and every stud/king/cripple that stops at its underside — clear of
      // the floor framing above, exactly like an explicit attach would).
      if (!ctx.topFloorBands.length) return [{ s0: run.s0, s1: run.s1, z0: ctx.prismTop, z1: ctx.prismTop }]
      const stations = [run.s0, ...ctx.topFloorBands.flatMap((b) => crossingStations(run, b.rings)).sort((a, b) => a - b), run.s1]
      const segs: VSeg[] = []
      for (let i = 0; i < stations.length - 1; i++) {
        const s0 = stations[i], s1 = stations[i + 1]
        if (s1 - s0 < 1e-4) continue
        const off = delta + oMid
        const mid: Pt2 = [
          run.aPt[0] + run.dir[0] * ((s0 + s1) / 2 - run.s0) + run.n[0] * off,
          run.aPt[1] + run.dir[1] * ((s0 + s1) / 2 - run.s0) + run.n[1] * off,
        ]
        const covering = ctx.topFloorBands.filter((b) => b.rings.some((r) => pointInRing(mid, r)))
        const z = covering.length ? Math.min(ctx.prismTop, ...covering.map((b) => b.zJoistBot)) : ctx.prismTop
        segs.push({ s0, s1, z0: z, z1: z })
      }
      return segs
    }
    const off = delta + oMid
    const at = (s: number): Pt2 => [
      run.aPt[0] + run.dir[0] * (s - run.s0) + run.n[0] * off,
      run.aPt[1] + run.dir[1] * (s - run.s0) + run.n[1] * off,
    ]
    const ringSets: Pt2[][] = []
    for (const surf of ctx.plateSurfs) {
      if (surf.kind === 'roof') ringSets.push(surf.ring)
      else ringSets.push(...surf.rings)
    }
    const stations = [run.s0, ...crossingStations(run, ringSets).sort((a, b) => a - b), run.s1]
    const segs: VSeg[] = []
    for (let i = 0; i < stations.length - 1; i++) {
      const s0 = stations[i], s1 = stations[i + 1]
      const L = s1 - s0
      if (L < 1e-4) continue
      const mid = at((s0 + s1) / 2)
      const gov = governingPlate(mid)
      if (!gov) { segs.push({ s0, s1, z0: ctx.prismTop, z1: ctx.prismTop }); continue }
      if (gov.kind === 'flat') { segs.push({ s0, s1, z0: gov.z, z1: gov.z }); continue }
      const slope = gov.m * (gov.uphill[0] * run.dir[0] + gov.uphill[1] * run.dir[1])
      if (Math.abs(slope) <= LEVEL_SLOPE_EPS) {
        const dBear = Math.abs((mid[0] - gov.origin[0]) * gov.uphill[0] + (mid[1] - gov.origin[1]) * gov.uphill[1])
        const z = dBear <= bearingSnapTol ? gov.bearingZ : gov.zAt(mid) + gov.framingRiseDv
        segs.push({ s0, s1, z0: z, z1: z })
      } else {
        // raked: two interior samples extrapolated (a station exactly on a ring
        // boundary never picks the wrong side — profileAlongRun's technique)
        const q1 = s0 + 0.25 * L, q2 = s0 + 0.75 * L
        const zq1 = gov.zAt(at(q1)) + gov.framingRiseDv
        const zq2 = gov.zAt(at(q2)) + gov.framingRiseDv
        const sl = (zq2 - zq1) / (0.5 * L)
        segs.push({ s0, s1, z0: zq1 - sl * 0.25 * L, z1: zq1 + sl * 0.75 * L })
      }
    }
    // no prismTop clamp here on purpose: a raked plate legitimately runs BELOW the
    // flat prism top where the wall reaches under a cropped eave band — the body
    // rises to the cropped mass underside there, the framing keeps the true plane
    return segs
  }
  const baseCap = ctx.prismTop - 0.1        // deriveSteppedBase's zCap, mirrored
  const zBotAtStation = (s: number): number => {
    for (const run of ctx.baseRuns) {
      if (s >= run.s0 - EPS && s <= run.s1 + EPS) return Math.min(run.z, baseCap)
    }
    return ctx.zBaseNominal
  }
  const zBotPtOf = (run: Run) => {
    if (ctx.baseRuns.length) {
      return (p: Pt2): number => {
        const s = run.s0 + (p[0] - run.aPt[0]) * run.dir[0] + (p[1] - run.aPt[1]) * run.dir[1]
        return zBotAtStation(s)
      }
    }
    if (ctx.baseSurfs.length) {
      return (p: Pt2): number => {
        for (const surf of ctx.baseSurfs) {
          for (const r of surf.rings) {
            if (pointInRing(p, r)) return Math.min(surf.zAt(p), ctx.prismBase)
          }
        }
        // the eave-wall cross-section-straddle lesson, mirrored on the base — a
        // base-attached wall's cross-section sliver
        // outside every target ring is still resting on the SAME base attach
        return ctx.prismBase
      }
    }
    return (): number => ctx.prismBase
  }

  // ---------- exact profile stations: surface-ring crossings of the core-center line ----------
  function crossingStations(run: Run, ringSets: Pt2[][]): number[] {
    const off = delta + oMid
    const P0: Pt2 = [run.aPt[0] + run.n[0] * off, run.aPt[1] + run.n[1] * off]
    const L = run.s1 - run.s0
    const D: Pt2 = [run.dir[0] * L, run.dir[1] * L]
    const out: number[] = []
    {
      for (const ring of ringSets) {
        for (let i = 0; i < ring.length; i++) {
          const A = ring[i], B = ring[(i + 1) % ring.length]
          const E: Pt2 = [B[0] - A[0], B[1] - A[1]]
          const den = D[0] * E[1] - D[1] * E[0]
          if (Math.abs(den) < 1e-12) continue
          // P0 + tSeg·D = A + tEdge·E, solved by 2D cross products (R = A − P0):
          // tSeg = (R×E)/(D×E), tEdge = (R×D)/(D×E)
          const dx = A[0] - P0[0], dy = A[1] - P0[1]
          const tSeg = (dx * E[1] - dy * E[0]) / den
          const tEdge = (dx * D[1] - dy * D[0]) / den
          if (tSeg > EPS && tSeg < 1 - EPS && tEdge >= -EPS && tEdge <= 1 + EPS) {
            out.push(run.s0 + tSeg * L)
          }
        }
      }
    }
    return out
  }

  /** piecewise-linear profile along a run over `surfs` (linear planes between ring
   *  crossings) — endpoint z's come from two interior samples extrapolated, so a
   *  station sitting exactly ON a surface boundary never picks the wrong side.
   *  A sample outside every ring (the same cross-section-straddles-the-bearing-line
   *  case `governingPlate` guards above) falls back to `bound` — the flat prism height —
   *  rather than the wall's raw nominal top/base, which is only right when there's
   *  no attach at all (where `bound` and nominal are already the same value). */
  function profileAlongRun(
    run: Run, surfs: FramingSurf[], bound: number, mode: 'max' | 'min',
  ): VSeg[] {
    const off = delta + oMid
    const at = (s: number): Pt2 => [
      run.aPt[0] + run.dir[0] * (s - run.s0) + run.n[0] * off,
      run.aPt[1] + run.dir[1] * (s - run.s0) + run.n[1] * off,
    ]
    const zRaw = (p: Pt2): number => {
      for (const surf of surfs) {
        for (const r of surf.rings) if (pointInRing(p, r)) return surf.zAt(p)
      }
      return bound
    }
    const stations = [run.s0, ...crossingStations(run, surfs.flatMap((s) => s.rings)).sort((a, b) => a - b), run.s1]
    const segs: VSeg[] = []
    for (let i = 0; i < stations.length - 1; i++) {
      const s0 = stations[i], s1 = stations[i + 1]
      const L = s1 - s0
      if (L < 1e-4) continue
      const q1 = s0 + 0.25 * L, q2 = s0 + 0.75 * L
      const zq1 = zRaw(at(q1)), zq2 = zRaw(at(q2))
      const slope = (zq2 - zq1) / (0.5 * L)
      segs.push({ s0, s1, z0: zq1 - slope * 0.25 * L, z1: zq1 + slope * 0.75 * L })
    }
    return clampSplit(segs, bound, mode)
  }

  // ---------- mesh emission ----------
  const acc = newMeshAcc()
  const edges: number[] = []

  function boxFor(
    run: Run, sa: number, sb: number,
    zBotOfRaw: (p: Pt2) => number, zTopOfRaw: (p: Pt2) => number,
  ): void {
    if (!(sb - sa > 1e-4)) return
    const base = (s: number): Pt2 => [run.aPt[0] + run.dir[0] * (s - run.s0), run.aPt[1] + run.dir[1] * (s - run.s0)]
    const off = (p: Pt2, o: number): Pt2 => [p[0] + run.n[0] * (delta + o), p[1] + run.n[1] * (delta + o)]
    let ring: Pt2[] = [off(base(sa), oExt), off(base(sb), oExt), off(base(sb), oInt), off(base(sa), oInt)]
    if (signedArea(ring) < 0) ring = [...ring].reverse()
    // pointwise evaluators sample a hair toward the member's own center — a ring
    // corner sitting EXACTLY on a clip-surface boundary (the end stud of a gable
    // wall at the roof's edge) would otherwise raycast ambiguously and read the
    // nominal height, poking the member through the plate line
    const cx = ring.reduce((s, p) => s + p[0], 0) / ring.length
    const cy = ring.reduce((s, p) => s + p[1], 0) / ring.length
    const nudge = (p: Pt2): Pt2 => {
      const d = Math.hypot(cx - p[0], cy - p[1])
      if (d < 1e-9) return p
      const k = Math.min(2e-3 / d, 0.5)
      return [p[0] + (cx - p[0]) * k, p[1] + (cy - p[1]) * k]
    }
    const zBotOf = (p: Pt2) => zBotOfRaw(nudge(p))
    const zTopOf = (p: Pt2) => Math.max(zTopOfRaw(nudge(p)), zBotOf(p))
    let h = 0
    for (const p of ring) h = Math.max(h, zTopOf(p) - zBotOf(p))
    if (h < MIN_MEMBER_FT) return
    // member outlines — the open-frame look reads on crisp edges, not shading alone
    // (prism()'s shared `edges` param — the roof-framing round's field-rafter fix)
    prism(acc, ring, zBotOf, zTopOf, edges)
  }

  for (const run of runs) {
    const runLen = run.s1 - run.s0
    if (runLen < w * 2) continue
    const zBotPt = zBotPtOf(run)
    const zTopOfSeg = (seg: VSeg) => (p: Pt2): number => {
      const s = run.s0 + (p[0] - run.aPt[0]) * run.dir[0] + (p[1] - run.aPt[1]) * run.dir[1]
      return lerpZ(seg, Math.max(seg.s0, Math.min(s, seg.s1)))
    }

    // the plate-line profile governs the double top plate AND every member's top
    // cut (PLATE-HEIGHT round) — computed once per run, up front
    const topSegs = plateProfileAlongRun(run)
    const plateTopAt = (p: Pt2): number => {
      const s = run.s0 + (p[0] - run.aPt[0]) * run.dir[0] + (p[1] - run.aPt[1]) * run.dir[1]
      for (const seg of topSegs) {
        if (s >= seg.s0 - EPS && s <= seg.s1 + EPS) return lerpZ(seg, Math.max(seg.s0, Math.min(s, seg.s1)))
      }
      if (topSegs.length) {
        const first = topSegs[0], last = topSegs[topSegs.length - 1]
        return s < first.s0 ? first.z0 : last.z1
      }
      return ctx.prismTop
    }

    // openings on this run (clamped to it) — jamb framing + plate cuts key off these.
    // VERTICALLY MULLED STACKS (2026-10-01, Matt — Santiago: a transom set directly on a
    // tall fixed window, "the wood headers won't go away even though the frames are
    // touching (suggesting they would be mulled)"): openings that overlap ALONG the
    // wall and whose frames TOUCH (within MULL_STACK_GAP_FT) frame as ONE unit — one
    // sill under the lowest, one header over the highest, kings/trimmers the full
    // height. Any real gap is a designed condition and keeps its own header and sill:
    // a transom set a few inches over a door, an Upper-level window over a Main-level
    // one (the STACKED OPENINGS zones below).
    const ops = mullVerticalStacks(
      placements.filter((pl) => pl.center > run.s0 - EPS && pl.center < run.s1 + EPS && pl.s1 - pl.s0 > EPS)
        // ARCHED / RAKED HEADS (2026-10-30, Matt): the header goes over the TOP of a shaped
        // head — the arch's crown, a rake's high side — not its springline, or the header
        // crosses the arch. The rough opening is framed square to that height; the arch
        // itself is formed inside it (zHead is otherwise the springline).
        .map((pl) => (pl.riseFt > 0 || pl.rakeFt ? { ...pl, zHead: pl.zHead + pl.riseFt + Math.abs(pl.rakeFt || 0) } : pl)),
      plateFt,
    ).sort((a, b) => a.s0 - b.s0)
    const doors = ops.filter((pl) => pl.zLow <= ctx.zBaseNominal + DOOR_SILL_TOL_FT)

    // mulled clusters (see "opening framing" below) — built up front so the
    // STACKED-OPENING zones can read each opening's header depth
    const MULL_GAP_FT = 4 * w
    const clusters: OpeningPlacement[][] = []
    {
      let curEnd = -Infinity
      for (const pl of ops) {
        const cur = clusters[clusters.length - 1]
        const vOverlap = cur?.some((c) => c.zLow < pl.zHead - EPS && pl.zLow < c.zHead - EPS)
        if (cur && pl.s0 - curEnd < MULL_GAP_FT - EPS && vOverlap) {
          cur.push(pl)
          curEnd = Math.max(curEnd, pl.s1)
        } else {
          clusters.push([pl])
          curEnd = pl.s1
        }
      }
    }
    // STACKED OPENINGS (2026-09-29, Santiago: an Upper-level window over a Main-level
    // window in one two-story wall): each opening's framed zone — sill plate bottom
    // (or the floor, for a door) up to its header top — which NO vertical member may
    // pass through, whichever opening that member was placed for
    const zones = clusters.flatMap((group) => {
      const g0 = Math.min(...group.map((q) => q.s0))
      const g1 = Math.max(...group.map((q) => q.s1))
      const zHeadTop = Math.max(...group.map((q) => q.zHead)) + headerRuleFor(g1 - g0).depthIn / 12
      return group.map((pl) => ({
        s0: pl.s0, s1: pl.s1,
        z0: pl.zLow <= ctx.zBaseNominal + DOOR_SILL_TOL_FT ? -Infinity : pl.zLow - plateFt,
        z1: zHeadTop,
      }))
    })
    // SPAN BEAMS: a beam at the base over each open span, bearing a stud width past each
    // end; no vertical member passes through it (an opening zone from the floor up)
    const beams: { s0: number; s1: number; zTop: number }[] = []
    for (const sp of ctx.spanBeams ?? []) {
      const lo = Math.max(run.s0, sp.s0 - w), hi = Math.min(run.s1, sp.s1 + w)
      if (hi - lo < 2 * w) continue
      const depth = headerRuleFor(Math.max(0, Math.min(sp.s1, run.s1) - Math.max(sp.s0, run.s0))).depthIn / 12
      const zTop = Math.min(ctx.prismBase + depth, ctx.prismTop - 3 * plateFt)
      if (zTop - ctx.prismBase < MIN_MEMBER_FT) continue
      beams.push({ s0: lo, s1: hi, zTop })
      zones.push({ s0: lo, s1: hi, z0: -Infinity, z1: zTop })
    }

    // ---------- bottom plate (single), following the derived base, cut at doors ----------
    let botSegs: VSeg[]
    if (ctx.baseRuns.length) {
      // stepped stem/curb tops: piecewise constant — cut at the layout's run stations
      botSegs = []
      const stations = [run.s0, run.s1]
      for (const r of ctx.baseRuns) {
        for (const s of [r.s0, r.s1]) if (s > run.s0 + EPS && s < run.s1 - EPS) stations.push(s)
      }
      stations.sort((a, b) => a - b)
      for (let i = 0; i < stations.length - 1; i++) {
        const s0 = stations[i], s1 = stations[i + 1]
        if (s1 - s0 < 1e-4) continue
        const z = zBotAtStation((s0 + s1) / 2)
        botSegs.push({ s0, s1, z0: z, z1: z })
      }
    } else if (ctx.baseSurfs.length) {
      botSegs = profileAlongRun(run, ctx.baseSurfs, ctx.prismBase, 'min')
    } else {
      botSegs = [{ s0: run.s0, s1: run.s1, z0: ctx.prismBase, z1: ctx.prismBase }]
    }
    // subtract door spans (the RO — kings/trimmers outside it keep their plate seat)
    for (const d of doors) {
      const next: VSeg[] = []
      for (const seg of botSegs) {
        if (d.s1 <= seg.s0 + EPS || d.s0 >= seg.s1 - EPS) { next.push(seg); continue }
        if (d.s0 > seg.s0 + 1e-4) next.push({ s0: seg.s0, s1: d.s0, z0: seg.z0, z1: lerpZ(seg, d.s0) })
        if (d.s1 < seg.s1 - 1e-4) next.push({ s0: d.s1, s1: seg.s1, z0: lerpZ(seg, d.s1), z1: seg.z1 })
      }
      botSegs = next
    }
    for (const b of beams) {
      const next: VSeg[] = []
      for (const seg of botSegs) {
        if (b.s1 <= seg.s0 + EPS || b.s0 >= seg.s1 - EPS) { next.push(seg); continue }
        if (b.s0 > seg.s0 + 1e-4) next.push({ s0: seg.s0, s1: b.s0, z0: seg.z0, z1: lerpZ(seg, b.s0) })
        if (b.s1 < seg.s1 - 1e-4) next.push({ s0: b.s1, s1: seg.s1, z0: lerpZ(seg, b.s1), z1: seg.z1 })
      }
      botSegs = next
      boxFor(run, b.s0, b.s1, () => ctx.prismBase, () => b.zTop)
    }
    for (const seg of botSegs) {
      const zB = zTopOfSeg(seg)
      boxFor(run, seg.s0, seg.s1, (p) => zB(p), (p) => zB(p) + plateFt)
    }

    // ---------- double top plate, following the PLATE RULE (level at the bearing
    // height on bearing stretches, raked at the main-body framing underside on
    // rake stretches — see plateProfileAlongRun) ----------
    for (const seg of topSegs) {
      const zT = zTopOfSeg(seg)
      boxFor(run, seg.s0, seg.s1, (p) => zT(p) - plateFt, (p) => zT(p))
      boxFor(run, seg.s0, seg.s1, (p) => zT(p) - 2 * plateFt, (p) => zT(p) - plateFt)
    }

    // stud vertical span: bottom-plate top → underside of the lower top plate,
    // cut-to-fit per point (a raked plate line rakes the stud's top cut)
    const studBot = (p: Pt2) => zBotPt(p) + plateFt
    const studTop = (p: Pt2) => plateTopAt(p) - 2 * plateFt

    // ---------- FRAMING SESSION Phase 3: PLATFORM FRAMING, derived ----------
    // Doctrine: BEARING INTENT IS DECLARED BY MODELED OVERLAP — a wood-framed
    // floor whose ring covers a stretch of this run (tested at the core-center
    // line, exactly like the plate rule) segments the derived framing there:
    // double CAP plates directly under the floor framing, a single SOLE plate
    // on the floor's sheathing, and NO vertical member passing through the
    // band. The wall element itself is untouched.
    const runBands: { band: FloorPlatformBand; ivs: [number, number][] }[] = []
    if (ctx.floorBands.length) {
      const off = delta + oMid
      const at = (s: number): Pt2 => [
        run.aPt[0] + run.dir[0] * (s - run.s0) + run.n[0] * off,
        run.aPt[1] + run.dir[1] * (s - run.s0) + run.n[1] * off,
      ]
      for (const band of ctx.floorBands) {
        const stations = [run.s0, ...crossingStations(run, band.rings).sort((a, b) => a - b), run.s1]
        const ivs: [number, number][] = []
        for (let i = 0; i < stations.length - 1; i++) {
          const s0 = stations[i], s1 = stations[i + 1]
          if (s1 - s0 < 1e-4) continue
          const mid = at((s0 + s1) / 2)
          if (!band.rings.some((r) => pointInRing(mid, r))) continue
          const last = ivs[ivs.length - 1]
          if (last && Math.abs(last[1] - s0) < 1e-6) last[1] = s1
          else ivs.push([s0, s1])
        }
        if (ivs.length) runBands.push({ band, ivs })
      }
      runBands.sort((a, b) => a.band.zJoistBot - b.band.zJoistBot)
      for (const { band, ivs } of runBands) {
        for (const [a0, a1] of ivs) {
          // FRAMED CRAWL SPACE follow-up: platform plates only where the derived
          // framing actually continues BELOW the band. A bearing stretch (the
          // stem risen to the mudsill seat, wall starting on the sheathing)
          // already carries its own bottom plate on the sheathing — cap plates
          // there would coincide with the mudsill and the sole would double the
          // bottom plate. The derived base is piecewise constant on the layout's
          // run stations, so split the interval there and test each piece.
          const capBot = band.zJoistBot - 2 * plateFt
          const stations: number[] = [a0, a1]
          for (const r of ctx.baseRuns) {
            for (const s of [r.s0, r.s1]) if (s > a0 + 1e-6 && s < a1 - 1e-6) stations.push(s)
          }
          stations.sort((x, y) => x - y)
          for (let i = 0; i + 1 < stations.length; i++) {
            const s0 = stations[i], s1 = stations[i + 1]
            if (s1 - s0 < 1e-4) continue
            if (zBotAtStation((s0 + s1) / 2) >= capBot - 1e-6) continue
            // double CAP plates directly under the floor framing…
            boxFor(run, s0, s1, () => capBot, () => band.zJoistBot - plateFt)
            boxFor(run, s0, s1, () => band.zJoistBot - plateFt, () => band.zJoistBot)
            // …and the single SOLE plate resting on the floor's SHEATHING
            boxFor(run, s0, s1, () => band.zSheathTop, () => band.zSheathTop + plateFt)
          }
        }
      }
    }
    const bandsAt = (s: number): FloorPlatformBand[] =>
      runBands.filter(({ ivs }) => ivs.some(([a0, a1]) => s >= a0 - EPS && s <= a1 + EPS)).map((rb) => rb.band)
    /** a vertical member (stud/king/trimmer/cripple) — split at every covering
     *  platform band so studs NEVER pass through the floor framing */
    const placedVert = new Set<string>()
    const vertBox = (sa: number, sb: number, botF: (p: Pt2) => number, topF: (p: Pt2) => number): void => {
      // STACKED OPENINGS: split around every opening zone this member crosses in s
      const hits = zones.filter((zn) => sa < zn.s1 - EPS && sb > zn.s0 + EPS).sort((a, b) => a.z0 - b.z0)
      if (hits.length) {
        let lower = botF
        for (const zn of hits) {
          const lo = lower
          if (zn.z0 > -Infinity) vertBoxRaw(sa, sb, lo, (p) => Math.min(topF(p), zn.z0))
          lower = (p) => Math.max(lo(p), zn.z1)
        }
        vertBoxRaw(sa, sb, lower, topF)
        return
      }
      vertBoxRaw(sa, sb, botF, topF)
    }
    const vertBoxRaw = (sa: number, sb: number, botF: (p: Pt2) => number, topF: (p: Pt2) => number): void => {
      // a member placed twice (a stacked pair's shared cripple bay) lands once
      const sm = (sa + sb) / 2
      const mid: Pt2 = [run.aPt[0] + run.dir[0] * (sm - run.s0), run.aPt[1] + run.dir[1] * (sm - run.s0)]
      const key = `${sa.toFixed(4)}|${sb.toFixed(4)}|${botF(mid).toFixed(4)}|${topF(mid).toFixed(4)}`
      if (placedVert.has(key)) return
      placedVert.add(key)
      // a segment the zone split squeezed to nothing (below a sill that starts under
      // this member's own bottom) is no member at all
      if (topF(mid) - botF(mid) < 1e-4) return
      const covering = bandsAt((sa + sb) / 2)
      if (!covering.length) { boxFor(run, sa, sb, botF, topF); return }
      let lower = botF
      for (const b of covering) {
        const cap = b.zJoistBot - 2 * plateFt
        boxFor(run, sa, sb, lower, (p) => Math.min(topF(p), cap))
        const soleTop = b.zSheathTop + plateFt
        lower = (p) => Math.max(botF(p), soleTop)
      }
      boxFor(run, sa, sb, lower, topF)
    }

    // ---------- studs from a consistent layout end (the run's start) ----------
    const framed = ops.map((pl) => [pl.s0 - 2 * w, pl.s1 + 2 * w] as [number, number])
    const clearOfFraming = (c: number) => framed.every(([lo, hi]) => c + w / 2 <= lo + EPS || c - w / 2 >= hi - EPS)
    const centers: number[] = []
    const endA = run.s0 + w / 2, endB = run.s1 - w / 2
    centers.push(endA)
    for (let k = 1; ; k++) {
      const c = run.s0 + k * spacingFt
      if (c > run.s1 - w) break
      if (c - endA < w || endB - c < w) continue
      centers.push(c)
    }
    if (endB - endA > w) centers.push(endB)
    for (const c of centers) {
      if (!clearOfFraming(c)) continue
      vertBox(c - w / 2, c + w / 2, studBot, studTop)
    }

    // ---------- FRAMED CRAWL SPACE round (2026-08-16, Matt): a vertical stud at
    // every STEM STEP — standing at the end of the lower step's sill plate, hard
    // against the riser face, running up the open stem to the plate line. The
    // field layout is spacing-driven from the run's start and generally never
    // lands a stud at the step station, leaving the bay edge unframed. Applies
    // to any stepped base (crawl-space steps and true curb steps alike).
    for (let i = 0; i + 1 < ctx.baseRuns.length; i++) {
      const a = ctx.baseRuns[i], b = ctx.baseRuns[i + 1]
      if (Math.abs(a.z - b.z) < 1e-6) continue
      const sB = a.s1
      if (sB <= run.s0 + w || sB >= run.s1 - w) continue
      // the stud stands on the LOWER side, abutting the riser
      const c = a.z < b.z ? sB - w / 2 : sB + w / 2
      if (!clearOfFraming(c)) continue
      if (centers.some((e) => Math.abs(e - c) < w)) continue
      vertBox(c - w / 2, c + w / 2, studBot, studTop)
    }

    // ---------- opening framing: king + trimmer + header (+ sill/cripples) ----------
    // MULLED-OPENINGS round (2026-08-19, Matt): openings too close together to fit
    // the jamb pack between them (trimmer + king each side = 4 stud widths) frame
    // as ONE unit — no intermediate kings/trimmers overlapping the neighbor's RO,
    // one continuous header across the full run, sized from the TOTAL mulled span.
    // Clustering requires VERTICAL overlap too, so a transom stacked above a door
    // (station-adjacent but not side-by-side) keeps its own independent framing.
    for (const group of clusters) {
      const clampS = (s: number) => Math.max(run.s0, Math.min(s, run.s1))
      const g0 = Math.min(...group.map((p) => p.s0))
      const g1 = Math.max(...group.map((p) => p.s1))
      // the continuous header clears EVERY head in the group (mulled units share
      // a head height in practice; a mixed group governs by the tallest)
      const zHead = Math.max(...group.map((p) => p.zHead))
      // kings — full studs hard against the trimmers, group ends only
      vertBox(clampS(g0 - 2 * w), clampS(g0 - w), studBot, studTop)
      vertBox(clampS(g1 + w), clampS(g1 + 2 * w), studBot, studTop)
      // trimmers — bottom plate top → the header seat (they carry the header)
      vertBox(clampS(g0 - w), clampS(g0), studBot, (p) => Math.min(zHead, studTop(p)))
      vertBox(clampS(g1), clampS(g1 + w), studBot, (p) => Math.min(zHead, studTop(p)))
      // header — the curated span rule sizes its depth from the FULL span; full core
      // thickness across; top caps at the plate underside (an opening head at the
      // plate line degenerates)
      const rule = headerRuleFor(g1 - g0)
      const headerTop = (p: Pt2) => Math.min(zHead + rule.depthIn / 12, Math.max(studTop(p), zHead))
      boxFor(run, clampS(g0 - w), clampS(g1 + w), () => zHead, headerTop)
      // cripples above the header at the field module
      for (let k = 0; ; k++) {
        const c = run.s0 + k * spacingFt
        if (c > g1 + w) break
        if (c - w / 2 < g0 - w || c + w / 2 > g1 + w) continue
        vertBox(c - w / 2, c + w / 2, headerTop, studTop)
      }
      // window: flat sill + cripples below it — per OPENING (each window keeps its
      // own sill; a mulled door beside it keeps its clear floor)
      for (const pl of group) {
        const isDoor = pl.zLow <= ctx.zBaseNominal + DOOR_SILL_TOL_FT
        if (isDoor) continue
        const sillBot = (p: Pt2) => Math.max(pl.zLow - plateFt, studBot(p))
        boxFor(run, clampS(pl.s0), clampS(pl.s1), sillBot, () => pl.zLow)
        for (let k = 0; ; k++) {
          const c = run.s0 + k * spacingFt
          if (c > pl.s1) break
          if (c - w / 2 < pl.s0 || c + w / 2 > pl.s1) continue
          vertBox(c - w / 2, c + w / 2, studBot, sillBot)
        }
      }
    }
  }

  if (acc.vc === 0) return null
  let zMin = Infinity, zMax = -Infinity
  for (let i = 2; i < acc.pos.length; i += 3) {
    if (acc.pos[i] < zMin) zMin = acc.pos[i]
    if (acc.pos[i] > zMax) zMax = acc.pos[i]
  }
  return {
    elementId: el.id,
    category: 'wall',
    outlines: [],                      // plan linework untouched — the body draws the wall
    zBase: zMin,
    zTop: zMax,
    materialId: framingMaterialId(el),
    groupKey: `wall|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|framing`,
    pieces: [],
    placements: [],
    openingInstances: [],
    mesh: registerCtx({
      positions: new Float32Array(acc.pos),
      normals: new Float32Array(acc.nrm),
      indices: new Uint32Array(acc.idx),
      edges,
      uvs: meshAccUvs(acc),
    }, ctx),
    meshOnly: true,
    skipProjectedOutline: true,
    // the one-wall-solid-per-element assumption breaker — plan's wall pass and the
    // corner-weld end collection skip framing solids on this flag (see the
    // ElementSolid.framing doc note)
    framing: true,
  }
}

/**
 * VERTICALLY MULLED STACKS: merge openings that sit one on another along the wall
 * (overlapping in s) when their frames TOUCH — the head of one within
 * MULL_STACK_GAP_FT of the next one's sill. That is a mulled unit: one rough opening.
 * The merged placement spans both in s and z and takes the UPPER opening's head shape
 * (arch/rake). `_plateFt` is kept for the call site's shape.
 */
export const MULL_STACK_GAP_FT = 1 / 12
export function mullVerticalStacks(list: OpeningPlacement[], _plateFt: number): OpeningPlacement[] {
  const out = list.map((pl) => ({ ...pl }))
  let merged = true
  while (merged) {
    merged = false
    outer: for (let i = 0; i < out.length; i++) {
      for (let j = 0; j < out.length; j++) {
        if (i === j) continue
        const lo = out[i], hi = out[j]
        const sOverlap = Math.min(lo.s1, hi.s1) - Math.max(lo.s0, hi.s0)
        if (sOverlap <= EPS) continue
        if (hi.zLow <= lo.zLow + EPS) continue    // not stacked above it
        // frame-inclusive voids of touching units can OVERLAP slightly — that is touching
        const gap = hi.zLow - lo.zHead
        if (gap > MULL_STACK_GAP_FT + EPS) continue   // a real gap: designed, framed between
        const s0 = Math.min(lo.s0, hi.s0), s1 = Math.max(lo.s1, hi.s1)
        out[i] = { ...hi, s0, s1, center: (s0 + s1) / 2, zLow: lo.zLow, zHead: Math.max(lo.zHead, hi.zHead) }
        out.splice(j, 1)
        merged = true
        break outer
      }
    }
  }
  return out
}
